import { Key, useEffect, useMemo, useState } from "react";
import AnalystConfiguration from "./analystConfiguration";
import AnalyticsDetails from "./analyticsDetails";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Breadcrumbs, BreadcrumbItem, Chip, Modal, ModalBody, ModalContent, ModalHeader } from "@nextui-org/react";
import { Icon } from "@iconify/react";
import { AuditLogsAgGridWrapper, ConvertToLocalDatePowerBI, CustomTabs, getAnalystViewAuditLogStaticFilters } from "@circuitry-ai/doc-ui";
import { Score } from '@circuitry-ai/doc-agent';
import Analystnotifications from "./analystnotifications";
import { RouteState, useEntityResourcePermissions, useGetAiWorkerByIdQuery } from "@circuitry-ai/doc-data";
import Analystweight from "./analystweight";
import EvaluationsDetails from "./evaluationsDetails";
import PyanalystDetails from "./pyanalystDetails";
import { AnalystDashboardRoute } from "./analystDashboardRoute";
/* eslint-disable-next-line */
export interface AnalystBoardProps {

}

function resolveDashboardHeading(worker?: { name?: string; description?: string } | null): string {
  const name = String(worker?.name ?? '').trim();
  if (name) return name;
  return String(worker?.description ?? '').trim();
}

export enum AnalystTabs {
  DASHBOARD = 'dashboard',
  CONFIGURATION = 'configuration',
  NOTIFICATION = 'notification',
  WEIGHT = 'weight',
  CLAIM_SCORE = 'claimScore',
  AUDIT_LOGS = 'audit-logs',
}

//TODO: Metrics is not implemented yet
//TODO: Error Loading component need to be integrated

export function AnalystBoard(props: AnalystBoardProps) {

  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const tabValue = searchParams.get('tab') ?? AnalystTabs.DASHBOARD;
  const reportId = searchParams.get('reportId');
  const aiworkderId = searchParams.get('aiworkerId');
  const analystType = searchParams.get('analystType');
  const reportType = searchParams.get('reportType');
  const isEvaluationsAnalyst = analystType === 'evaluations';
  const isPyAnalyst = analystType === 'pyanalyst';
  const isDecisionAnalyst = analystType === 'decision';
  const { canUpdate } = useEntityResourcePermissions('analyst', aiworkderId ?? undefined);

  const isCreateAnalyst = location.pathname.includes('create');
  const { report } = location?.state ?? {};
  const headerAiworkerId = aiworkderId ?? report?.aiworker_id ?? null;
  const { data: aiWorkerFromQuery, isLoading: isLoadingWorker } = useGetAiWorkerByIdQuery(headerAiworkerId ?? '', {
    skip: isCreateAnalyst || !headerAiworkerId,
    refetchOnMountOrArgChange: true,
  });

  const analystViewAuditLogFilters = useMemo(
    () => (headerAiworkerId ? getAnalystViewAuditLogStaticFilters(headerAiworkerId) : []),
    [headerAiworkerId]
  );

  const [currentTab, setCurrentTab] = useState<string>(tabValue ?? AnalystTabs.CONFIGURATION);
  const [lastRefreshedReportData, setLastRefreshedReportData] = useState<any>();
  const [refreshStatus, setRefreshStatus] = useState<'idle' | 'success' | 'error' | 'loading'>('idle');
  const [isDashboardFullscreenOpen, setIsDashboardFullscreenOpen] = useState(false);

  const dashboardHeading = useMemo(
    () => resolveDashboardHeading(report ?? aiWorkerFromQuery),
    [report, aiWorkerFromQuery]
  );

  useEffect(() => {
    if (tabValue) {
      setCurrentTab(tabValue);
    } else {
      setCurrentTab(AnalystTabs.CONFIGURATION);
    }
  }, [tabValue]);

  const canViewTab = (condition: boolean) => !isCreateAnalyst && condition;
  const hasReportId = !!reportId;

  const dashboardFullScreen = () => {
    setIsDashboardFullscreenOpen(true);
  };

  const renderDashboardContent = () => {
    if (isEvaluationsAnalyst) {
      return <EvaluationsDetails />;
    }
    if (isPyAnalyst) {
      return <PyanalystDetails />;
    }
    // The fetched aiworker first: router state can predate a switch of its runtime.
    return (
      <AnalystDashboardRoute worker={aiWorkerFromQuery ?? report} isDecision={isDecisionAnalyst} isLoadingWorker={isLoadingWorker}>
        <AnalyticsDetails
          setLastRefreshedReportData={setLastRefreshedReportData}
          setRefreshStatus={setRefreshStatus}
          lastRefreshedReportData={lastRefreshedReportData}
        />
      </AnalystDashboardRoute>
    );
  };

  const tabItems = [
    { key: AnalystTabs.CONFIGURATION, title: 'Configuration', id: 'configuration-tab' },
    { key: AnalystTabs.NOTIFICATION, title: 'Notification', id: 'notification-tab', canView: canViewTab(hasReportId && !isEvaluationsAnalyst && !isPyAnalyst && !isDecisionAnalyst) },
    { key: AnalystTabs.WEIGHT, title: 'Weight', id: 'weight-tab', canView: canViewTab(!hasReportId && !isEvaluationsAnalyst && !isPyAnalyst && !isDecisionAnalyst) },
    {
      key: AnalystTabs.DASHBOARD,
      title: 'Dashboard',
      id: 'dashboard-tab',
      canView: canViewTab(hasReportId || isEvaluationsAnalyst || isPyAnalyst || isDecisionAnalyst),
      endContent: <Icon icon="material-symbols:fullscreen" width="18" height="18" onClick={(event) => {
        event.stopPropagation();
        dashboardFullScreen();
      }} />
    },
    { key: AnalystTabs.CLAIM_SCORE, title: 'Claim Score', id: 'claim_score-tab', canView: canViewTab(!hasReportId && !isEvaluationsAnalyst && !isPyAnalyst && !isDecisionAnalyst) },
    { key: AnalystTabs.AUDIT_LOGS, title: 'Audit Logs', id: 'analyst-view-audit-logs-tab', canView: canViewTab(true) },
  ];

  const renderSelectedTab = () => {
    switch (tabValue) {
      case AnalystTabs.CONFIGURATION:
        return <AnalystConfiguration />;
      case AnalystTabs.NOTIFICATION:
        return <Analystnotifications />;
      case AnalystTabs.WEIGHT:
        return <Analystweight />;
      case AnalystTabs.DASHBOARD:
        return renderDashboardContent();
      case AnalystTabs.CLAIM_SCORE:
        return <Score isDisabled={!canUpdate} />;
      case AnalystTabs.AUDIT_LOGS:
        return headerAiworkerId ? (
          <div className=" pt-2">
            <AuditLogsAgGridWrapper staticFilters={analystViewAuditLogFilters} />
          </div>
        ) : null;
    }
  };

  const onTabChange = (tab: Key) => {
    setCurrentTab(tab as string);
    const params = new URLSearchParams({ tab: String(tab), aiworkerId: aiworkderId ?? '' });
    if (reportId) params.append('reportId', reportId);
    if (isEvaluationsAnalyst || isPyAnalyst || isDecisionAnalyst) params.append('analystType', analystType ?? '');
    if (isEvaluationsAnalyst && reportType) params.append('reportType', reportType);

    const { report } = location?.state ?? {};
    navigate(`/analytics/${RouteState.VIEW}?${params.toString()}`, { state: { report } });
  };

  let disabledKeys: AnalystTabs[];
  if (isCreateAnalyst) {
    disabledKeys = [AnalystTabs.NOTIFICATION, AnalystTabs.WEIGHT, AnalystTabs.DASHBOARD, AnalystTabs.CLAIM_SCORE, AnalystTabs.AUDIT_LOGS];
  } else if (canUpdate) {
    disabledKeys = [];
  } else {
    disabledKeys = [AnalystTabs.CONFIGURATION, AnalystTabs.NOTIFICATION, AnalystTabs.WEIGHT];
  }
  if (isEvaluationsAnalyst || isPyAnalyst || isDecisionAnalyst) {
    disabledKeys = [...disabledKeys, AnalystTabs.NOTIFICATION, AnalystTabs.WEIGHT, AnalystTabs.CLAIM_SCORE];
  }

  useEffect(() => {
    if (!isCreateAnalyst && disabledKeys.includes(currentTab as AnalystTabs)) {
      const safeTab = (hasReportId || isEvaluationsAnalyst || isPyAnalyst || isDecisionAnalyst) ? AnalystTabs.DASHBOARD : AnalystTabs.CLAIM_SCORE;
      onTabChange(safeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledKeys, currentTab, isCreateAnalyst, hasReportId, isEvaluationsAnalyst, isDecisionAnalyst]);

  useEffect(() => {
    if (!isCreateAnalyst && disabledKeys.includes(currentTab as AnalystTabs)) {
      const safeTab = hasReportId ? AnalystTabs.DASHBOARD : AnalystTabs.CLAIM_SCORE;
      onTabChange(safeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabledKeys, currentTab, isCreateAnalyst, hasReportId]);

  // Render refresh status message
  const renderRefreshStatusMessage = () => {
    switch (refreshStatus) {
      case 'success':
        return (
          <div className="bg-success/10 text-success py-1 px-2 flex items-center w-full gap-2 text-[12px] rounded-[16px] border border-success">
            <Chip id="dashboard-refresh-status" color={'success'} size="sm" variant="flat" className="bg-content1 border border-success">Success</Chip>
            <span id="dashboard-refresh-timing">
            Last refreshed on {ConvertToLocalDatePowerBI(lastRefreshedReportData?.endTime ?? '')}
            </span>
          </div>
        );
      case 'error':
        return (
          <div className="bg-danger/10 text-danger py-1 px-2 rounded-md flex items-center w-full text-sm">
            Refresh failed. Try again.
          </div>
        );
      case 'loading':
        return (
          <div className="bg-primary/10 text-primary py-1 px-2 rounded-md flex items-center w-full text-sm">
            <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Fetching latest data...
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="analyst-board flex flex-col h-full gap-[12px] bg-greesn-200 pt-4">
      <div className='flex flex-col-reverse gap-3 lg:flex-row justify-between items-center'>
        <div className="flex flex-row items-center gap-3">
          {tabValue === AnalystTabs.DASHBOARD ?
            <>
              <div className="whitespace-nowrap text-xl font-medium">{dashboardHeading}</div>
              {renderRefreshStatusMessage()}
            </> : <div className="whitespace-nowrap text-xl font-medium">{`${isCreateAnalyst ? 'Create' : 'Update'} Analyst`}</div>
          }
        </div>
        <Breadcrumbs key={'Analysts'} size={'md'} separator={'>'} underline={'active'} color={'primary'} >
          <BreadcrumbItem onPress={() => navigate('/analytics')}><Icon icon="carbon:home" width="18" height="18" /></BreadcrumbItem>
          <BreadcrumbItem onPress={() => navigate('/analytics')}>Analysts</BreadcrumbItem>
          <BreadcrumbItem className="font-semibold">{tabValue.charAt(0).toUpperCase() + tabValue.slice(1)}</BreadcrumbItem>
        </Breadcrumbs>
      </div>
      <div className="advisor-tab-list">
        <CustomTabs
          tabs={tabItems}
          selectedKey={currentTab}
          onTabChange={onTabChange}
          disabledKeys={disabledKeys}
        />
      </div>
      <div className="flex-1 overflow-y-auto" style={{ height: `calc(100vh - 150px)` }}>{renderSelectedTab()}</div>
      <Modal
        isOpen={isDashboardFullscreenOpen}
        onOpenChange={setIsDashboardFullscreenOpen}
        size="full"
        classNames={{
          wrapper: "p-0 w-screen h-screen max-w-none max-h-none min-w-full min-h-full",
          base: "w-full h-full max-w-none max-h-none rounded-none m-0 flex flex-col",
          body: "p-0 flex-1 min-h-0 overflow-hidden",
          header: "border-b border-default-200",
        }}
        scrollBehavior="inside"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center justify-between px-5 py-2">
                <span>Dashboard</span>
              </ModalHeader>
              <ModalBody>
                <div className="h-full overflow-auto px-[12px] lg:px-[24px] py-3 full-screen-analyst-dashboard">
                  {renderDashboardContent()}
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}

export default AnalystBoard;
